export const ADMIN_DEFAULT_TAB = "home";

const TAB_PATHS = {
  home: "/home",
  announcements: "/announcements",
  suggestions: "/suggestions",
  scheduling__calendar: "/scheduling/schedule",
  scheduling__logs: "/scheduling/activity-logs",
  scheduling__subjects: "/scheduling/subjects",
  student__directory: "/students/directory",
  scheduling__student: "/students/calendar",
  student__plans: "/students/plans",
  dsat__access: "/diagnostics/access",
  diag__reports: "/diagnostics/reports",
  diag__archived: "/diagnostics/archived",
  diag__content: "/diagnostics/content",
  dsat__report: "/diagnostics/sat/report",
  dsat__archived: "/diagnostics/sat/archived",
  dsat__content: "/diagnostics/sat/content",
  psat__report: "/diagnostics/psat/report",
  psat__content: "/diagnostics/psat/content",
  act__report: "/diagnostics/act/report",
  act__archived: "/diagnostics/act/archived",
  act__content: "/diagnostics/act/content",
  curriculum: "/curriculum",
  employee__directory: "/employee/directory",
  employee__availability: "/employee/calendar",
  employee__rate: "/employee/pay-rate",
  employee__payroll: "/employee/payroll",
  employee__logging: "/employee/log-hours",
  session__log: "/session/log",
  session__progress: "/session/progress-report",
  account: "/account",
  access_control: "/access-control",
  settings: "/settings",
};

const DEFAULT_ROUTE_ORDER = [
  "home",
  "scheduling__calendar",
  "announcements",
  "suggestions",
  "scheduling__logs",
  "scheduling__subjects",
  "student__directory",
  "scheduling__student",
  "student__plans",
  "dsat__access",
  "diag__reports",
  "diag__archived",
  "diag__content",
  "dsat__report",
  "dsat__archived",
  "dsat__content",
  "psat__report",
  "psat__content",
  "act__report",
  "act__archived",
  "act__content",
  "curriculum",
  "employee__directory",
  "employee__availability",
  "employee__rate",
  "employee__payroll",
  "employee__logging",
  "session__log",
  "session__progress",
  "account",
  "access_control",
  "settings",
];

export const ADMIN_PORTAL_ROUTE_PATHS = [
  "/admin",
  "/home",
  "/announcements",
  "/suggestions",
  "/scheduling",
  "/scheduling/*",
  "/students",
  "/students/*",
  "/diagnostics",
  "/diagnostics/*",
  "/curriculum",
  "/curriculum/*",
  "/employee",
  "/employee/*",
  "/session",
  "/session/*",
  "/account",
  "/access-control",
  "/settings",
];

const PRESERVED_PATH_PREFIXES = [
  "/students/directory/",
  "/students/calendar",
  "/employee/directory/",
  "/employee/payroll",
];

function normalizePathname(pathname = "") {
  const trimmed = String(pathname || "").trim();
  if (!trimmed) return "/";
  if (trimmed === "/") return "/";
  return trimmed.replace(/\/+$/, "");
}

export function getAdminPathForTab(tabKey) {
  return TAB_PATHS[tabKey] || null;
}

/**
 * Where clicking a nav item should go when its section is ALREADY open.
 *
 * Returns the section's root path when the current URL is deeper than that root
 * — a student profile at /students/directory/<id>, a curriculum page at
 * /curriculum/ap/<book> — so clicking "Directory" again returns to the full
 * list. Returns null when we are already at the root, which tells the caller to
 * keep the existing behaviour of re-mounting the page (a manual refresh).
 */
export function getAdminSectionRootRedirect(tabKey, pathname = "") {
  const root = getAdminPathForTab(tabKey);
  if (!root) return null;
  const here = String(pathname || "").trim().replace(/\/+$/, "") || "/";
  return here === root ? null : root;
}

export function getAdminTabFromPath(pathname = "") {
  const normalized = normalizePathname(pathname);

  if (normalized === "/" || normalized === "/admin" || normalized === "/home") return ADMIN_DEFAULT_TAB;
  if (normalized === "/announcements") return "announcements";
  if (normalized === "/suggestions") return "suggestions";

  if (normalized === "/scheduling" || normalized.startsWith("/scheduling/schedule")) {
    return "scheduling__calendar";
  }
  if (normalized === "/scheduling/activity-logs") return "scheduling__logs";
  if (normalized === "/scheduling/subjects") return "scheduling__subjects";

  if (normalized === "/students" || normalized === "/students/directory" || normalized.startsWith("/students/directory/")) {
    return "student__directory";
  }
  if (normalized === "/students/calendar") return "scheduling__student";
  if (normalized === "/students/plans") return "student__plans";

  if (normalized === "/diagnostics" || normalized === "/diagnostics/reports") return "diag__reports";
  if (normalized === "/diagnostics/archived") return "diag__archived";
  if (normalized === "/diagnostics/access") return "dsat__access";
  if (normalized === "/diagnostics/content") return "diag__content";
  if (normalized === "/diagnostics/sat/report") return "dsat__report";
  if (normalized === "/diagnostics/sat/archived") return "dsat__archived";
  if (normalized === "/diagnostics/sat/content") return "dsat__content";
  if (normalized === "/diagnostics/psat/report") return "psat__report";
  if (normalized === "/diagnostics/psat/content") return "psat__content";
  if (normalized === "/diagnostics/act/report") return "act__report";
  if (normalized === "/diagnostics/act/archived") return "act__archived";
  if (normalized === "/diagnostics/act/content") return "act__content";

  if (normalized === "/curriculum" || normalized.startsWith("/curriculum/")) return "curriculum";
  if (normalized === "/employee" || normalized === "/employee/directory" || normalized.startsWith("/employee/directory/")) {
    return "employee__directory";
  }
  if (normalized === "/employee/calendar") return "employee__availability";
  if (normalized === "/employee/pay-rate") return "employee__rate";
  if (normalized === "/employee/payroll") return "employee__payroll";
  if (normalized === "/employee/log-hours") return "employee__logging";

  if (normalized === "/session" || normalized === "/session/log") return "session__log";
  if (normalized === "/session/progress-report") return "session__progress";

  if (normalized === "/account") return "account";
  if (normalized === "/access-control") return "access_control";
  if (normalized === "/settings") return "settings";

  return null;
}

export function getCanonicalAdminPath(pathname = "", activeTab = ADMIN_DEFAULT_TAB) {
  const normalized = normalizePathname(pathname);

  if (normalized === "/employee/clock" || normalized === "/employee/clock-in-out") {
    return TAB_PATHS.employee__directory;
  }
  if (normalized.startsWith("/announcements/")) return TAB_PATHS.announcements;
  // Curriculum owns its own sub-routing (hub / library / textbook). Preserve the
  // full path verbatim so deep links like /curriculum/ap/<textbookId> survive.
  if (normalized === "/curriculum" || normalized.startsWith("/curriculum/")) return normalized;
  if (normalized === "/scheduling") return TAB_PATHS.scheduling__calendar;
  if (normalized === "/students") return TAB_PATHS.student__directory;
  if (normalized === "/diagnostics") return TAB_PATHS.diag__reports;
  if (normalized === "/employee") return TAB_PATHS.employee__directory;
  if (normalized === "/session") return TAB_PATHS.session__log;
  if (normalized === "/" || normalized === "/admin") {
    return getAdminPathForTab(activeTab) || TAB_PATHS[ADMIN_DEFAULT_TAB];
  }

  if (PRESERVED_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return normalized;
  }

  const tabKey = getAdminTabFromPath(normalized);
  if (!tabKey) return null;
  if (tabKey === "scheduling__calendar") return normalized;
  return getAdminPathForTab(tabKey);
}

export function getFallbackAdminPath(blockedPages = []) {
  const blockedSet = new Set(Array.isArray(blockedPages) ? blockedPages : []);
  const nextTab = DEFAULT_ROUTE_ORDER.find((tabKey) => !blockedSet.has(tabKey));
  return getAdminPathForTab(nextTab || ADMIN_DEFAULT_TAB) || TAB_PATHS[ADMIN_DEFAULT_TAB];
}

export function getGroupKeyForAdminTab(tabKey = "") {
  // "scheduling__student" (the "Calendar" item) lives under the Students group,
  // not Scheduling, so it must be resolved before the prefix rules below.
  if (tabKey === "scheduling__student") return "student";

  if (tabKey.startsWith("scheduling__")) return "scheduling";
  if (tabKey.startsWith("employee__")) return "employee";
  if (tabKey.startsWith("session__")) return "session";
  if (tabKey.startsWith("student__")) return "student";
  if (tabKey.startsWith("dsat__") || tabKey.startsWith("act__") || tabKey.startsWith("psat__") || tabKey.startsWith("diag__")) return "dsat";
  return null;
}

// Paths whose query string (e.g. ?studentId=&attemptId=) must survive the
// canonical-path redirect. The diagnostic report/archive pages are opened as
// deep links from the unified Diagnostic Reports page, so their search params
// must be preserved or the target report won't load.
const PRESERVE_SEARCH_PATHS = new Set([
  "/students/calendar",
  "/diagnostics/access",
  "/diagnostics/reports",
  "/diagnostics/sat/report",
  "/diagnostics/sat/archived",
  "/diagnostics/psat/report",
  "/diagnostics/act/report",
  "/diagnostics/act/archived",
  "/employee/payroll",
]);

// Student-detail paths keep their query string so the Plan tab's ?plan=<id>
// deep link (plan rows, the Plans hub, "edit plan" round-trips) resolves to that
// plan's detail view instead of being stripped.
const PRESERVE_SEARCH_PREFIXES = ["/students/directory/"];

export function shouldPreserveAdminSearch(pathname = "") {
  const p = normalizePathname(pathname);
  return (
    PRESERVE_SEARCH_PATHS.has(p) || PRESERVE_SEARCH_PREFIXES.some((prefix) => p.startsWith(prefix))
  );
}
